import React, { useMemo } from "react";

import Select from "antd/es/select";
import { useIntl } from "react-intl";

import FieldCategory from "@mapmycustomers/shared/enum/fieldModel/FieldCategory";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FilterOption from "@mapmycustomers/shared/enum/fieldModel/FilterOption";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { EntityTypeSupportingHistory } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { CategorizedFields } from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import {
  IFilterComponentProps,
  IFilterInstance,
} from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import defaultFilterOption from "@mapmycustomers/shared/util/input/defaultFilterOption";

import { getFieldCategoryDisplayName } from "@app/util/ui";

import getFieldModelByEntityType from "../../fieldModel/getByEntityType";
import { isCustomField } from "../../fieldModel/impl/assert";
import { getEmptyCategorizedFields } from "../../fieldModel/impl/FieldModel";

import styles from "./HistoryField.module.scss";

export const HISTORY_FIELD_FILTER_OPERATORS = [FilterOperator.IN_ANY];

const doesSupportValue = (value: any, operator: FilterOperator) =>
  Array.isArray(value) &&
  value.every((item) => typeof item === "number") &&
  operator === FilterOperator.IN_ANY;

interface HistoryFieldFilterProps extends IFilterComponentProps {}

const HistoryFieldFilter: IFilterInstance = {
  doesSupportOption: (option: FilterOption) => option === FilterOption.ENTITY_TYPE,
  doesSupportValue,
  getComponent:
    (): ((props: HistoryFieldFilterProps) => JSX.Element) =>
    ({ disabled, onChange, options, placeholder, value }: HistoryFieldFilterProps) => {
      const intl = useIntl();

      const categorizedFields: CategorizedFields = useMemo(() => {
        if (!options?.entityType) {
          return getEmptyCategorizedFields();
        }

        const result = getFieldModelByEntityType(
          options.entityType as EntityTypeSupportingHistory
        ).categorizedFields;

        Object.keys(result).forEach((category) => {
          result[category as FieldCategory] = result[category as FieldCategory] = result[
            category as FieldCategory
          ].filter(
            (field) => !isCustomField(field) && field.hasFeature(FieldFeature.HISTORY_FIELD)
          );
        });
        return result;
      }, [options?.entityType]);

      const selectOptions = useMemo(() => {
        const options = (Object.keys(categorizedFields) as FieldCategory[])
          .filter((category) => categorizedFields[category].length)
          .map((category) => ({
            label: getFieldCategoryDisplayName(intl, category),
            options: categorizedFields[category].map((field) => ({
              label: field.hasFeature(FieldFeature.ADDRESS)
                ? intl.formatMessage({
                    id: "filters.historyField.addressField",
                    defaultMessage: "Address",
                    description: "Single address field on history field filter",
                  })
                : field.displayName,
              value: field.platformFilterNames[0],
            })),
          }));

        const customFieldLabel = intl.formatMessage({
          id: "filters.historyField.customFields",
          defaultMessage: "Custom Fields",
          description: "Custom Fields label on in history field filter",
        });
        options.push({
          label: customFieldLabel,
          options: [{ label: customFieldLabel, value: "value" }],
        });

        return options;
      }, [intl, categorizedFields]);

      return (
        <Select<IField["name"][]>
          className={styles.container}
          disabled={disabled}
          filterOption={defaultFilterOption}
          maxTagCount="responsive"
          mode="multiple"
          onChange={(fieldNames: IField["name"][]) => onChange?.({ ...value, value: fieldNames })}
          options={selectOptions}
          placeholder={
            placeholder ??
            intl.formatMessage({
              id: "filters.historyField.placeholder",
              defaultMessage: "Click or type to select",
              description: "Custom Fields placeholder on history field filter",
            })
          }
          value={Array.isArray(value.value) ? value.value : undefined}
        />
      );
    },
};

export default HistoryFieldFilter;
